import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

import { getTeamMembers } from '../../services/teamService';
import { cn } from '../../utils/cn';

const TeamMemberSelector = ({ isOpen, onClose, onSelect, title = 'Select Team Member', showAvailability = false }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');

  useEffect(() => {
    if (isOpen) {
      loadTeamMembers();
    }
  }, [isOpen]);

  const loadTeamMembers = async () => {
    setLoading(true);
    try {
      const members = await getTeamMembers();
      setTeamMembers(members || []);
    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { label: 'Active', className: 'bg-success/10 text-success', icon: 'CheckCircle' },
      inactive: { label: 'Inactive', className: 'bg-muted text-muted-foreground', icon: 'Circle' },
      on_leave: { label: 'On Leave', className: 'bg-warning/10 text-warning', icon: 'Calendar' }
    };
    return badges?.[status] || badges?.active;
  };

  const filteredMembers = teamMembers?.filter(member => {
    if (member?.isPendingInvite) return false;
    const matchesSearch = member?.fullName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         member?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         member?.role?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesRole = selectedRole === 'all' || member?.role === selectedRole;
    return matchesSearch && matchesRole;
  });

  const handleSelect = (member) => {
    onSelect?.(member);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[300] p-4" onClick={onClose}>
      <div className="bg-card rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={(e) => e?.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-accent p-6 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">{title}</h2>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <Icon name="X" size={20} className="text-white" />
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-border space-y-4">
          <div className="relative">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name, email, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'admin', 'operator', 'viewer']?.map((role) => (
              <button
                key={role}
                onClick={() => setSelectedRole(role)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                  selectedRole === role
                    ? "bg-primary text-white" :"bg-muted text-muted-foreground hover:bg-muted/80"
                )}
              >
                {role?.charAt(0)?.toUpperCase() + role?.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Team Members List */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading team members...</div>
          ) : filteredMembers?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Users" size={48} className="mx-auto mb-3 opacity-50" />
              <p>No team members found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMembers?.map((member) => {
                const statusBadge = getStatusBadge(member?.status);
                return (
                  <button
                    key={member?.id}
                    onClick={() => handleSelect(member)}
                    className="w-full bg-muted/50 rounded-lg p-4 hover:bg-muted transition-colors text-left"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          {member?.avatarUrl ? (
                            <img
                              src={member?.avatarUrl}
                              alt={member?.fullName}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <Icon name="User" size={24} className="text-primary" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground mb-1">{member?.fullName}</h4>
                          <p className="text-sm text-muted-foreground">{member?.email}</p>
                          {member?.jobTitle && (
                            <p className="text-xs text-muted-foreground mt-1">{member?.jobTitle}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={cn(
                          "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium",
                          statusBadge?.className
                        )}>
                          <Icon name={statusBadge?.icon} size={12} />
                          {statusBadge?.label}
                        </span>
                        <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-md font-medium">
                          {member?.role?.charAt(0)?.toUpperCase() + member?.role?.slice(1)}
                        </span>
                      </div>
                    </div>
                    {showAvailability && member?.department && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Icon name="Briefcase" size={12} />
                            <span>{member?.department}</span>
                          </div>
                          {member?.phone && (
                            <div className="flex items-center gap-1">
                              <Icon name="Phone" size={12} />
                              <span>{member?.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamMemberSelector;